import { Pagination } from "../pages/Disputes";
import { AlertLog } from "./autoRefund";

export const AlertStatus = {
  ALL: 'all',
  PENDING: 'pending',
  RESOLVED: 'resolved',
} as const;

export type AlertStatus = typeof AlertStatus[keyof typeof AlertStatus] | string;

/**
 * Alert source values. Mirrors apps/api/types/chargebackHelp.ts.
 */
export const AlertSource = {
  ETHOCA: 'ethoca',
  VERIFI_RDR: 'verifi_rdr',
  VERIFI_CDRN: 'verifi_cdrn',
} as const;

export type AlertSource = typeof AlertSource[keyof typeof AlertSource];

export const AlertType = {
  FRAUD: 'Fraud',
  DISPUTE: 'Dispute',
  OTHER: 'Other',
} as const;

export type AlertType = typeof AlertType[keyof typeof AlertType] | string;

export interface EthocaAlert {
  // Internal primary key
  alert_id: number;

  // Ethoca alert id (from ChargebackHelp metadata.external_id)
  ethoca_alert_id: string | null;

  // Company
  company_id: number | null;
  company_name: string | null;
  brand_name: string | null;
  ethoca_visa_refund_enabled?: boolean;

  // Payment processor mapping (replaces company_id)
  pp_type: string | null;
  pp_account_id: number | null;

  // Merchant Info
  merchant_id: number | null;
  merchant_name: string | null;

  // Descriptor Info
  descriptor_id: number | null;
  descriptor_name: string | null;
  main_descriptor_id: number | null;
  main_descriptor_name: string | null;

  // Alert Classification
  source: string;
  type: string | null;
  status: string;
  outcome: string | null;

  // Card Information (minimal - for matching)
  card_bin: string | null;
  card_last4: string | null;

  // Transaction Identifier (for matching)
  auth_code: string | null;
  transaction_id: string | null;

  // Key Timestamps
  alert_timestamp: Date | string | null;
  transaction_timestamp: Date | string | null;

  // Full API response (all other fields available here)
  metadata: Record<string, any> | null;

  // Key fields extracted from metadata
  arn: string | null;
  alert_amount: number | null;
  alert_currency: string | null;
  refund_max_amount?: number | null;
  amount_exceeds_limit?: boolean;
  resolved_over_max_amount?: boolean;
  card_brand: string | null;
  resolved_at: string | null;
  billed_at: string | null;
  refunded: string | null;
  transaction_type: string | null;

  // CM Status (internal lifecycle)
  cm_status: AlertCmStatus;
  cm_notes: string | null;
  cm_status_changed_at: Date | string;
  refund_verification_failed?: boolean;
  staleness: 'ok' | 'warning' | 'critical';
  age_hours: number;

  // Subscription status (independent of cm_status)
  subscription_status: AlertSubscriptionStatus;
  subscription_status_changed_at: Date | string;

  // Matched order
  matched_order_url: string | null;
  matched_gateway_title: string | null;
  google_sheet_mid: string | null;
  fulfillment_status: string | null;

  // Match verification
  match_verification_status: MatchVerificationStatus | null;
  match_verification_notes: string | null;
  match_verified_at: string | null;
  match_verified_by: string | null;

  // Assignment (admin/agent view only)
  assigned_user_id: number | null;
  assigned_first_name: string | null;
  assigned_email: string | null;

  // Internal timestamps
  created_at: Date | string;
  updated_at: Date | string;
  logs: AlertLog[] | [];
}

export const AlertCmStatus = {
  QUEUED: 'queued',
  ACTION_NEEDED: 'action_needed',
  AUTO_RESOLVED: 'auto_resolved',
  RESOLVED: 'resolved',
} as const;

export type AlertCmStatus = typeof AlertCmStatus[keyof typeof AlertCmStatus] | string;

export const CM_STATUS_FILTER_OPTIONS: { value: AlertCmStatus; label: string }[] = [
  { value: AlertCmStatus.QUEUED, label: 'Queued For Automation' },
  { value: AlertCmStatus.ACTION_NEEDED, label: 'Action Needed' },
  { value: AlertCmStatus.RESOLVED, label: 'Resolved' },
  { value: AlertCmStatus.AUTO_RESOLVED, label: 'Auto Resolved' },
];

export const CM_STATUS_FILTER_VALUES = CM_STATUS_FILTER_OPTIONS.map(o => o.value);

export const DEFAULT_CM_STATUS_FILTER: AlertCmStatus[] = [AlertCmStatus.ACTION_NEEDED];

export function parseCmStatusFilter(param: string | null): AlertCmStatus[] {
  if (param === null) return [...DEFAULT_CM_STATUS_FILTER];
  return param.split(',').filter(v => CM_STATUS_FILTER_VALUES.includes(v));
}

// Returns the URL value, or null when the selection matches the default (so the param is omitted).
export function serializeCmStatusFilter(values: string[]): string | null {
  const isDefault = values.length === 1 && values[0] === AlertCmStatus.ACTION_NEEDED;
  if (isDefault) return null;
  return values.length > 0 ? values.join(',') : 'all';
}

/**
 * Subscription status (independent of cm_status).
 *   action_needed (initial)
 *     → queued                                       (RDR auto-cancel ingestion picked it up)
 *     → cancelled | not_subscription | not_found    (manually set)
 *     → auto_cancelled | auto_not_subscription      (set by the Loop auto-cancel hook)
 */
export const AlertSubscriptionStatus = {
  ACTION_NEEDED: 'action_needed',
  QUEUED: 'queued',
  CANCELLED: 'cancelled',
  NOT_SUBSCRIPTION: 'not_subscription',
  NOT_FOUND: 'not_found',
  AUTO_CANCELLED: 'auto_cancelled',
  AUTO_NOT_SUBSCRIPTION: 'auto_not_subscription',
} as const;

export type AlertSubscriptionStatus = typeof AlertSubscriptionStatus[keyof typeof AlertSubscriptionStatus] | string;

export const AlertSubscriptionStatusLabel: Record<string, string> = {
  [AlertSubscriptionStatus.ACTION_NEEDED]: 'Action Needed',
  [AlertSubscriptionStatus.QUEUED]: 'Queued',
  [AlertSubscriptionStatus.CANCELLED]: 'Cancelled',
  [AlertSubscriptionStatus.AUTO_CANCELLED]: 'Cancelled (Auto)',
  [AlertSubscriptionStatus.NOT_SUBSCRIPTION]: 'Not a Subscription',  
  [AlertSubscriptionStatus.AUTO_NOT_SUBSCRIPTION]: 'Not a Subscription (Auto)',
  [AlertSubscriptionStatus.NOT_FOUND]: 'Not Found',
};

/**
 * Statuses available for manual selection in the subscription status dialogs.
 * AUTO_* values are excluded because they are set by the system (Loop auto-cancel hook).
 */
export const MANUAL_SUBSCRIPTION_STATUS_OPTIONS = [
  AlertSubscriptionStatus.ACTION_NEEDED,
  AlertSubscriptionStatus.CANCELLED,
  AlertSubscriptionStatus.NOT_SUBSCRIPTION,
  AlertSubscriptionStatus.NOT_FOUND,
] as const;

/**
 * All subscription status values available as list-page filter options.
 * Includes AUTO_* and QUEUED values (set by system) so operators can slice by automation outcome.
 */
export const ALL_SUBSCRIPTION_STATUS_FILTER_OPTIONS = [
  AlertSubscriptionStatus.ACTION_NEEDED,
  AlertSubscriptionStatus.QUEUED,
  AlertSubscriptionStatus.CANCELLED,
  AlertSubscriptionStatus.AUTO_CANCELLED,
  AlertSubscriptionStatus.NOT_SUBSCRIPTION,
  AlertSubscriptionStatus.AUTO_NOT_SUBSCRIPTION,
  AlertSubscriptionStatus.NOT_FOUND,
] as const;

export const SUBSCRIPTION_STATUS_FILTER_OPTIONS: { value: string; label: string }[] =
  ALL_SUBSCRIPTION_STATUS_FILTER_OPTIONS.map(v => ({ value: v, label: AlertSubscriptionStatusLabel[v] }));

export const SUBSCRIPTION_STATUS_FILTER_VALUES = SUBSCRIPTION_STATUS_FILTER_OPTIONS.map(o => o.value);

export const FulfillmentStatus = {
  CANCELLED: 'cancelled',
  NOT_CANCELLED: 'not_cancelled',
  FULFILLED: 'fulfilled',
  PARTIALLY_FULFILLED: 'partially_fulfilled',
  NOT_APPLICABLE: 'not_applicable',
} as const;

export type FulfillmentStatus = typeof FulfillmentStatus[keyof typeof FulfillmentStatus] | string;

export const FulfillmentStatusLabel: Record<string, string> = {
  [FulfillmentStatus.CANCELLED]: 'Cancelled',
  [FulfillmentStatus.NOT_CANCELLED]: 'Not Cancelled',
  [FulfillmentStatus.FULFILLED]: 'Fulfilled',
  [FulfillmentStatus.PARTIALLY_FULFILLED]: 'Partially Fulfilled',
  [FulfillmentStatus.NOT_APPLICABLE]: 'Not Applicable',
};

export const FULFILLMENT_STATUS_OPTIONS = [
  FulfillmentStatus.CANCELLED,
  FulfillmentStatus.NOT_CANCELLED,
  FulfillmentStatus.FULFILLED,
  FulfillmentStatus.PARTIALLY_FULFILLED,
  FulfillmentStatus.NOT_APPLICABLE,
] as const;

export const TagsStatus = {
  ADDED: 'added',
  NOT_APPLICABLE: 'not_applicable',
} as const;

export type TagsStatus = typeof TagsStatus[keyof typeof TagsStatus] | string;

export const TagsStatusLabel: Record<string, string> = {
  [TagsStatus.ADDED]: 'Added',
  [TagsStatus.NOT_APPLICABLE]: 'Not Applicable',
};

export const TAGS_STATUS_OPTIONS = [
  TagsStatus.ADDED,
  TagsStatus.NOT_APPLICABLE,
] as const;

export const RefundedExternally = {
  YES: 'yes',
  NO: 'no',
  NOT_APPLICABLE: 'not_applicable',
} as const;

export type RefundedExternally = typeof RefundedExternally[keyof typeof RefundedExternally] | string;

export const RefundedExternallyLabel: Record<string, string> = {
  [RefundedExternally.YES]: 'Yes',
  [RefundedExternally.NO]: 'No',
  [RefundedExternally.NOT_APPLICABLE]: 'Not applicable',
};

export const REFUNDED_EXTERNALLY_OPTIONS = [
  RefundedExternally.NOT_APPLICABLE,
  RefundedExternally.YES,
  RefundedExternally.NO,
];

// Filter-only: matches RDR notices whose refunded_externally is still unset (NULL).
// Not offered in the resolve/edit dialogs — a value can only be set, not cleared to NULL.
export const REFUNDED_EXTERNALLY_NOT_SET = 'not_set';

export const REFUNDED_EXTERNALLY_FILTER_OPTIONS: { value: string; label: string }[] = [
  ...[RefundedExternally.YES, RefundedExternally.NO, RefundedExternally.NOT_APPLICABLE].map(
    v => ({ value: v, label: RefundedExternallyLabel[v] }),
  ),
  { value: REFUNDED_EXTERNALLY_NOT_SET, label: 'Not set' },
];

export const REFUNDED_EXTERNALLY_FILTER_VALUES = REFUNDED_EXTERNALLY_FILTER_OPTIONS.map(o => o.value);

export const RDR_CASE_EVENT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'NEW', label: 'New' },
  { value: 'TIMEOUT', label: 'Timeout' },
  { value: 'DELETE', label: 'Delete' },
];

export const RDR_CASE_EVENT_FILTER_VALUES = RDR_CASE_EVENT_FILTER_OPTIONS.map(o => o.value);

export const OUTCOME_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'DECLINED', label: 'Declined' },
];

export const OUTCOME_FILTER_VALUES = OUTCOME_FILTER_OPTIONS.map(o => o.value);

export const MatchVerificationStatus = {
  CORRECT: 'correct',
  INCORRECT: 'incorrect',
} as const;

export type MatchVerificationStatus = typeof MatchVerificationStatus[keyof typeof MatchVerificationStatus];

export interface AlertsResponse {
  alerts: EthocaAlert[];
  pagination: Pagination;
}

export const FraudAlertResolveStatus = {
  STOPPED: 'stopped',
  PARTIALLY_STOPPED: 'partially_stopped',
  PREVIOUSLY_CANCELLED: 'previously_cancelled',
  MISSED: 'missed',
  NOTFOUND: 'notfound',
  ACCOUNT_SUSPENDED: 'account_suspended',
  IN_PROGRESS: 'in_progress',
  SHIPPER_CONTACTED: 'shipper_contacted',
  OTHER: 'other',
} as const;

export type FraudAlertResolveStatus = typeof FraudAlertResolveStatus[keyof typeof FraudAlertResolveStatus] | string;

export const DisputeAlertResolveStatus = {
  RESOLVED: 'resolved',
  PREVIOUSLY_REFUNDED: 'previously_refunded',
  UNRESOLVED_DISPUTE: 'unresolved_dispute',
  NOTFOUND: 'notfound',
  OTHER: 'other',
} as const;

export type DisputeAlertResolveStatus = typeof DisputeAlertResolveStatus[keyof typeof DisputeAlertResolveStatus] | string;

export const AlertResolveRefunded = {
  REFUNDED: 'refunded',
  NOT_REFUNDED: 'not refunded',
  NOT_SETTLED: 'not settled',
} as const;

export type AlertResolveRefunded = typeof AlertResolveRefunded[keyof typeof AlertResolveRefunded] | string;

export const CdrnDeclineStatusCode = {
  GENERAL: '900',              // Unmatched Alert - General/Other
  NOT_PARTICIPATING: '901',    // Unmatched Alert - Merchant not Participating
  CANNOT_LOCATE: '902',        // Unmatched Alert - Unable to locate Original Transaction
  DUPLICATE: '940',            // Duplicate Request
  ACCOUNT_CLOSED: '950',       // Merchant account closed
  CHARGEBACK_RECEIVED: '952',  // Transaction Previously received a Chargeback
  OUTSIDE_TIMEFRAME: '953',    // Request is outside of eligibility timeframe
  THREEDS_AUTHENTICATED: '954',    // Transaction 3D Secure authenticated
  CANNOT_CANCEL: '955',        // Could not honor cancel request
  CANNOT_STOP_FULFILLMENT: '956', // Matched, unable to stop order fulfillment
  PROCEED_CHARGEBACK: '957',   // Matched, Proceed with Chargeback
} as const;

export type CdrnDeclineStatusCode = typeof CdrnDeclineStatusCode[keyof typeof CdrnDeclineStatusCode];

export const CdrnResolveStatusCode = {
    CREDIT_AND_CANCEL: '100',        // Credit & Cancellation processed
    PARTIAL_CREDIT: '101',           // Partial Credit & Cancellation processed
    AUTH_CANCELLED: '102',           // Authorization Cancelled
    PREVIOUSLY_CREDITED: '951',      // Transaction Previously credited
} as const;

export type CdrnResolveStatusCode = typeof CdrnResolveStatusCode[keyof typeof CdrnResolveStatusCode];

export const CdrnPaymentType = {
  VISA: 'VI',
  MASTERCARD: 'MC',
  AMERICAN_EXPRESS: 'AM',
  DISCOVER: 'DI',
} as const;

export type CdrnPaymentType = typeof CdrnPaymentType[keyof typeof CdrnPaymentType];

export const CdrnPaymentTypeLabel: Record<string, string> = {
  [CdrnPaymentType.VISA]: 'Visa',
  [CdrnPaymentType.MASTERCARD]: 'MasterCard',
  [CdrnPaymentType.AMERICAN_EXPRESS]: 'American Express',
  [CdrnPaymentType.DISCOVER]: 'Discover',
};

/**
 * CDRN Case Reason Codes per card network
 * Source: Verifi CDRN API Integration Guide, Appendix C (API 1.5, August 2024)
 */
export const CdrnReasonCodes: Record<string, Record<string, string>> = {
  [CdrnPaymentType.VISA]: {
    '0': 'Other / Unspecified',
    '10': 'Fraud Category',
    '10.1': 'EMV Liability Shift Counterfeit Fraud',
    '10.2': 'EMV Liability Shift Non-Counterfeit Fraud',
    '10.3': 'Other Fraud-Card Present Environment',
    '10.4': 'Other Fraud-Card Absent Environment',
    '10.5': 'Visa Fraud Monitoring Program',
    '11': 'Authorization Category',
    '11.1': 'Card Recovery Bulletin',
    '11.2': 'Declined Authorization',
    '11.3': 'No Authorization',
    '12': 'Processing Errors Category',
    '12.1': 'Late Presentment',
    '12.2': 'Incorrect Transaction Code',
    '12.3': 'Incorrect Currency',
    '12.4': 'Incorrect Account Number',
    '12.5': 'Incorrect Amount',
    '12.6': 'Duplicate Processing/Paid by Other Means',
    '12.6.1': 'Duplicate Processing',
    '12.6.2': 'Paid by Other Means',
    '12.7': 'Invalid Data',
    '13': 'Consumer Disputes Category',
    '13.1': 'Merchandise/Services Not Received',
    '13.2': 'Cancelled Recurring Transaction',
    '13.3': 'Not as Described or Defective Merchandise/Services',
    '13.4': 'Counterfeit Merchandise',
    '13.5': 'Misrepresentation',
    '13.6': 'Credit Not Processed',
    '13.7': 'Cancelled Merchandise/Services',
    '13.8': 'Original Credit Transaction Not Accepted',
    '13.9': 'Non-Receipt of Cash or Load Transaction Value',
    '30': 'Service Not Provided or Merchandise Not Received',
    '41': 'Cancelled Recurring Transaction',
    '53': 'Not as Described or Defective Merchandise',
    '57': 'Fraudulent Multiple Transactions',
    '60': 'Requested Copy Illegible or Invalid',
    '71': 'Declined Authorization',
    '72': 'No Authorization',
    '73': 'Expired Card',
    '74': 'Late Presentment',
    '75': 'Cardholder Does Not Recognize Transaction',
    '76': 'Incorrect Currency or Transaction Code or Domestic Processing Violation',
    '77': 'Non-Matching Account Number',
    '79': 'Requested Transaction Information Not Received',
    '80': 'Incorrect Transaction Amount or Account Number',
    '82': 'Duplicate Processing',
    '83': 'Fraudulent Transaction',
    '85': 'Credit Not Processed',
    '86': 'Paid by Other Means',
  },
  [CdrnPaymentType.MASTERCARD]: {
    '0': 'Other / Unspecified',
    '1': 'Non-Receipt of Requested Item',
    '2': 'Requested Copy Illegible or Invalid',
    '8': 'Requested/Required Authorization Not Obtained',
    '12': 'Non-Matching Account Number',
    '31': 'Incorrect Transaction Amount or Account Number',
    '34': 'Duplicate Processing',
    '35': 'Expired Card',
    '37': 'Fraudulent Transaction',
    '41': 'Cancelled Recurring Transaction',
    '42': 'Late Presentment',
    '46': 'Incorrect Currency or Transaction Code or Domestic Processing Violation',
    '50': 'Credit Posted as Debit',
    '53': 'Not as Described or Defective Merchandise',
    '54': 'Cardholder Dispute, Not Elsewhere Classified (other)',
    '55': 'Services Not Provided or Merchandise Not Received',
    '57': 'Credit Card Activated Telephone Transaction',
    '59': 'Paid by Other Means',
    '60': 'Credit Not Processed',
    '63': 'Cardholder Does Not Recognize',
  },
  [CdrnPaymentType.AMERICAN_EXPRESS]: {
    'M49': 'Vehicle Rental - Theft or Loss of Use',
    'P01': 'Unassigned Card Number',
    'P03': 'Credit Processed as Charge',
    'P04': 'Charge Processed as Credit',
    'P05': 'Incorrect Charge Amount',
    'P07': 'Late Submission',
    'P08': 'Duplicate Charge',
    'P22': 'Nonmatching Card Number',
    'P23': 'Currency Discrepancy',
    'R03': 'Insufficient Reply',
    'C02': 'Credit (or partial credit) Not Processed',
    'C04': 'Goods/Services Returned or Refused',
    'C05': 'Goods/Services Cancelled',
    'C08': 'Goods/Services Not Received',
    'C14': 'Paid by Other Means',
    'C18': '"No Show" or CARDeposit Cancelled',
    'C28': 'Cancelled Recurring Billing',
    'C31': 'Goods/Services Not as Described',
    'C32': 'Goods/Services Damaged or Defective',
    'M10': 'Vehicle Rental - Capital Damages',
    'R13': 'No Reply',
    'M01': 'Chargeback Authorization',
    'FR2': 'Fraud Full Recourse Program',
    'FR4': 'Immediate Chargeback Program',
    'FR6': 'Partial Immediate Chargeback Program',
  },
  [CdrnPaymentType.DISCOVER]: {
    '0': 'Other / Unspecified',
    'AA': 'Does Not Recognize',
    'AL': 'Airline Transaction Dispute',
    'AP': 'Recurring Payments',
    'AT': 'Authorization Related',
    'AW': 'Altered Amount',
    'CA': 'Cash Advance Dispute',
    'CD': 'Credit/Debit Posted Incorrectly',
    'CR': 'Cancelled Reservation',
    'DP': 'Duplicate Posting',
    'IC': 'Illegible Sales Data',
    'IN': 'Invalid Cardholder Number',
    'IS': 'Missing Signature',
    'LP': 'Late Presentation',
    'NC': 'Not Classified',
    'NR': 'Non Response to Ticket Retrieval Request',
    'RG': 'Non-Receipt of Goods or Services',
    'RM': 'Cardholder Disputes Quality of Goods or Services',
    'RN2': 'Credit Not Processed',
    'SV': 'Stored Value Dispute',
    'TF': 'Violation of Operating Regulations',
    'UA01': 'Fraud-Card Present Transaction',
    'UA02': 'Fraud-Card Not Present Transaction',
    'UA03': 'Fraud-Processing Error',
    'UANR': 'Fraud-Non Response to Ticket Retrieval Request',
  },
};

export const getCdrnReasonDescription = (
  paymentType: string | null,
  reasonCode: string | null,
): string | null => {
  if (!reasonCode) return null;
  if (paymentType && CdrnReasonCodes[paymentType]) {
    return CdrnReasonCodes[paymentType][reasonCode] ?? null;
  }
  // Fallback: search all networks
  for (const codes of Object.values(CdrnReasonCodes)) {
    if (codes[reasonCode]) return codes[reasonCode];
  }
  return null;
};

/**
 * Verifi status code descriptions (shared by CDRN and RDR)
 */
export const VerifiStatusCodeDescriptions: Record<string, string> = {
  // Resolve
  '100': 'Credit & Cancellation processed',
  '101': 'Partial Credit & Cancellation processed',
  '102': 'Authorization Cancelled',
  '103': 'Accepted',
  '951': 'Transaction Previously credited, no balance remaining',
  // Decline
  '900': 'Unmatched Case - General/Other',
  '901': 'Unmatched Case - Merchant not Participating',
  '902': 'Unmatched Case - Unable to locate Original Transaction',
  '940': 'Duplicate Request',
  '950': 'Merchant account closed, unable to process Credit',
  '952': 'Transaction Previously received a Chargeback, no balance remaining',
  '953': 'Request is outside of eligibility timeframe',
  '954': 'Transaction 3D Secure authenticated (Verified by Visa/MC SecureCode)',
  '955': 'Could not honor cancel request',
  '956': 'Matched, unable to stop order fulfillment',
  '957': 'Matched, Proceed with Chargeback for resolution',
  // Cancel
  '130': 'Cancellation processed',
};

export const formatVerifiStatusCode = (statusCode: string | null | undefined): string => {
  if (!statusCode) return '--';
  const description = VerifiStatusCodeDescriptions[statusCode];
  return description ? `${description} (${statusCode})` : statusCode;
};

export const formatVerifiReasonCode = (
  reasonCode: string | null | undefined,
  paymentType?: string | null,
): string => {
  if (!reasonCode) return '--';
  const description = getCdrnReasonDescription(paymentType ?? null, reasonCode);
  return description ? `${reasonCode} - ${description}` : reasonCode;
};

/**
 * Alert status lifecycle
 */
export const CdrnAlertStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  RESOLVED: 'RESOLVED',
  DECLINED: 'DECLINED',
  CANCELLED: 'CANCELLED',
  EXPORTING: 'EXPORTING',
  CLOSED: 'CLOSED',
} as const;

export type CdrnAlertStatus = typeof CdrnAlertStatus[keyof typeof CdrnAlertStatus];

export interface CdrnAlert {
  verifi_cdrn_alert_id: number;
  alert_key: string;
  alert_type: string;
  status: CdrnAlertStatus;
  status_code: string | null;

  // CM Status (internal lifecycle)
  cm_status: AlertCmStatus | string;
  refund_verification_failed?: boolean;
  cm_notes: string | null;
  cm_status_changed_at: Date | string;

  // Subscription status (independent of cm_status)
  subscription_status: AlertSubscriptionStatus;
  subscription_status_changed_at: Date | string;

  // Company
  company_id: number | null;
  company_name: string | null;
  brand_name: string | null;

  // Descriptor mapping
  verifi_cdrn_descriptor_id: number | null;
  descriptor_name: string | null;
  descriptor_contact: string | null;

  // Merchant name (from JOIN)
  merchant_name: string | null;

  reference_transaction_date: string | null;
  reference_transaction_amount: number | null; // cents
  reference_transaction_currency: string | null;
  refund_max_amount?: number | null;
  amount_exceeds_limit?: boolean;
  resolved_over_max_amount?: boolean;
  card_bin: string | null;
  card_last4: string | null;
  auth_code: string | null;
  verifi_merchant_key: string | null;  // Merchant key from the alert
  received_at: Date | string | null;
  resolved_at: Date | string | null;
  billed_at: string | null;
  metadata: Record<string, any> | null;
  reason_code: string | null;
  payment_type: CdrnPaymentType | string | null;
  match_method: string | null;
  verifi_cdrn_merchant_id: number | null;  // FK to matched merchant mapping
  pp_type: string | null;                   // Matched payment processor type
  pp_account_id: number | null;             // Matched payment processor account ID
  arn: string | null;
  network_timestamp: Date | string;
  age_hours: number;
  matched_order_url: string | null;
  matched_gateway_title: string | null;
  google_sheet_mid: string | null;
  fulfillment_status: string | null;

  // Match verification
  match_verification_status: MatchVerificationStatus | null;
  match_verification_notes: string | null;
  match_verified_at: string | null;
  match_verified_by: string | null;

  // Assignment (admin/agent view only)
  assigned_user_id: number | null;
  assigned_first_name: string | null;
  assigned_email: string | null;

  created_at: Date | string;
  updated_at: Date | string;
  verifiUrl: string | null;
  logs: AlertLog[] | [];
}

export interface CdrnMerchantFilterItem {
  merchant_id: number;
  merchant_name: string;
}

export interface CdrnDescriptorFilterItem {
  descriptor_id: number;
  descriptor_name: string;
}

export interface CdrnAlertsResponse {
  alerts: CdrnAlert[];
  pagination: Pagination;
}

// Client API response types (metadata stripped)
export interface ClientEthocaAlert extends Omit<EthocaAlert,
  'metadata' | 'cm_status' | 'cm_status_changed_at' |
  'subscription_status' | 'subscription_status_changed_at' |
  'pp_type' | 'pp_account_id' | 'staleness' | 'ethoca_visa_refund_enabled'> {
  metadata: null;
}

export interface ClientAlertsResponse {
  alerts: ClientEthocaAlert[];
  pagination: Pagination;
}

export interface ClientCdrnAlert extends Omit<CdrnAlert,
  'metadata' | 'cm_status' | 'cm_status_changed_at' |
  'subscription_status' | 'subscription_status_changed_at' |
  'pp_type' | 'pp_account_id' | 'match_method' > {
  metadata: null;
}

export interface ClientCdrnAlertsResponse {
  alerts: ClientCdrnAlert[];
  pagination: Pagination;
}

export interface SingleAlertResult {
  skipped?: boolean;
  reason?: string;
  status?: string;
  errorMessage?: string;
}

export interface AutoResolveOutcome {
  success: boolean;
  message?: string;
}

export function isResolved(status: AlertCmStatus | string): boolean {
  if (!status) return false;
  status = status as AlertCmStatus;
  return status === AlertCmStatus.RESOLVED || status === AlertCmStatus.AUTO_RESOLVED;
}
